import React, { useState, useEffect } from 'react';
import { Users, Plus, Trash2, ToggleLeft, ToggleRight, X } from 'lucide-react';
import { toast } from 'sonner';
import { signaturesAPI } from '../utils/api';

const SignerManagement = () => {
  const [signers, setSigners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSigner, setNewSigner] = useState({ name: '', title: '', email: '' });
  const [addingLoading, setAddingLoading] = useState(false);

  useEffect(() => {
    loadSigners();
  }, []);

  const loadSigners = async () => {
    try {
      const response = await signaturesAPI.getSigners();
      setSigners(response.data);
    } catch (error) {
      console.error('Failed to load signers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSigner = async (e) => {
    e.preventDefault();
    if (!newSigner.name || !newSigner.title || !newSigner.email) {
      toast.error('Please fill in all fields');
      return;
    }

    setAddingLoading(true);
    try {
      await signaturesAPI.addSigner(newSigner);
      toast.success('Signer added successfully');
      setNewSigner({ name: '', title: '', email: '' });
      setShowAddModal(false);
      loadSigners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add signer');
    } finally {
      setAddingLoading(false);
    }
  };

  const handleToggleSigner = async (signerId) => {
    try {
      const response = await signaturesAPI.toggleSigner(signerId);
      toast.success(response.data.message);
      loadSigners();
    } catch (error) {
      toast.error('Failed to toggle signer status');
    }
  };

  const handleDeleteSigner = async (signerId) => {
    if (!window.confirm('Are you sure you want to delete this signer?')) return;
    
    try {
      await signaturesAPI.deleteSigner(signerId);
      toast.success('Signer deleted successfully');
      loadSigners();
    } catch (error) {
      toast.error('Failed to delete signer');
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-slate-600">Loading signers...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Authorized Signers</h2>
          <p className="text-sm text-slate-600">Manage principle owners and authorized signatories</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="primary-button flex items-center gap-2"
        >
          <Plus size={18} />
          Add Signer
        </button>
      </div>

      {/* Signers List */}
      {signers.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-lg">
          <Users className="mx-auto text-slate-400 mb-4" size={48} />
          <p className="text-slate-600">No authorized signers configured</p>
          <button 
            onClick={() => setShowAddModal(true)}
            className="mt-4 text-purple-600 hover:text-purple-700 font-medium"
          >
            Add your first signer
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {signers.map((signer) => (
            <div 
              key={signer.id} 
              className={`border rounded-lg p-4 flex justify-between items-center transition-all ${
                signer.is_active 
                  ? 'border-slate-200 bg-white' 
                  : 'border-slate-200 bg-slate-50 opacity-75'
              }`}
            >
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${
                  signer.is_active ? 'bg-purple-100 text-purple-700' : 'bg-slate-200 text-slate-500'
                }`}>
                  {signer.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">{signer.name}</h3>
                  <p className="text-sm text-slate-600">{signer.title}, ReserveRelease.AI</p>
                  <p className="text-xs text-slate-500">{signer.email}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                  signer.is_active 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-slate-200 text-slate-600'
                }`}>
                  {signer.is_active ? 'Active' : 'Inactive'}
                </span>
                
                <button
                  onClick={() => handleToggleSigner(signer.id)}
                  className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                  title={signer.is_active ? 'Deactivate' : 'Activate'}
                >
                  {signer.is_active ? <ToggleRight size={20} className="text-green-600" /> : <ToggleLeft size={20} />}
                </button>
                
                <button
                  onClick={() => handleDeleteSigner(signer.id)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete signer"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Signer Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6 border-b border-slate-200 flex justify-between items-center">
              <h3 className="text-lg font-bold text-slate-900">Add Authorized Signer</h3>
              <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleAddSigner} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={newSigner.name}
                  onChange={(e) => setNewSigner({ ...newSigner, name: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  placeholder="e.g., Padraig Deighan"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Title *</label>
                <input
                  type="text"
                  value={newSigner.title}
                  onChange={(e) => setNewSigner({ ...newSigner, title: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  placeholder="e.g., CCO, CEO, Director"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
                <input
                  type="email"
                  value={newSigner.email}
                  onChange={(e) => setNewSigner({ ...newSigner, email: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  placeholder="email@reserverelease.ai"
                  required
                />
              </div>
              
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
                  disabled={addingLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 primary-button py-2"
                  disabled={addingLoading}
                >
                  {addingLoading ? 'Adding...' : 'Add Signer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignerManagement;
